import { useMemo, useRef, useState } from "react";
import { readStoredUser } from "../../utils/sessionUser";
import useOcrUploadQueue from "./useOcrUploadQueue";

export const invoicePath = "/api/purchasing/supplier-invoices";
export const validInvoiceFile = file => file?.size > 0 && file.size <= 50 * 1024 * 1024 && /\.(pdf|jpe?g|png|tiff?|heic|heif)$/i.test(file.name);
const may = (user, action) => Number(user?.id) === 1 || user?.permissions?.[`purchasing.supplier_invoices.${action}`] === true;
const canRead = user => may(user, "view_tab") && may(user, "update");
const canUpload = user => canRead(user) && may(user, "create");

export default function useInvoiceBackgroundUploads({ api, businessUnitId, blocked, user }) {
  const [revision, setRevision] = useState(0);
  const [duplicateId, setDuplicateId] = useState(null);
  const pendingReads = useRef(new Set());
  const [readingIds, setReadingIds] = useState(new Set());
  const invoiceApi = useMemo(() => ({
    isCurrent: api.isCurrent,
    async post(url, body, options) {
      if (url.endsWith("/upload") && !validInvoiceFile(body.get("file"))) {
        const error = "Choose a PDF or image scan of up to 50 MB.";
        throw Object.assign(new Error(error), { response: { status: 400, data: { error } } });
      }
      try {
        return await api.post(url, body, { ...options, params: { business_unit_id: businessUnitId } });
      } catch (failure) {
        if (api.isCurrent() && canUpload(readStoredUser()) && failure.response?.data?.code === "SUPPLIER_INVOICE_DUPLICATE_FILE") {
          setDuplicateId(failure.response.data.invoice_id);
        }
        throw failure;
      }
    },
  }), [api, businessUnitId]);
  const queue = useOcrUploadQueue({ api: invoiceApi, basePath: invoicePath, retryFailedReads: false, enabled: !blocked && canUpload(user),
    canContinue: () => canUpload(readStoredUser()), onRefresh: () => setRevision(value => value + 1) });
  const busyIds = useMemo(() => new Set([...readingIds, ...queue.rows
    .filter(row => row.importId && ["queued", "reading"].includes(row.status))
    .map(row => row.importId)]), [queue.rows, readingIds]);

  async function readInvoice(id, force) {
    if (!api.isCurrent() || !canRead(readStoredUser()) || busyIds.has(id) || pendingReads.current.has(id)) return;
    pendingReads.current.add(id);
    setReadingIds(new Set(pendingReads.current));
    try {
      const { data } = await invoiceApi.post(`${invoicePath}/${id}/process`, { force });
      if (api.isCurrent()) setRevision(value => value + 1);
      return data;
    } finally {
      pendingReads.current.delete(id);
      if (api.isCurrent()) setReadingIds(new Set(pendingReads.current));
    }
  }
  return { queue, busyIds, readInvoice, revision, duplicateId, clearDuplicate: () => setDuplicateId(null) };
}
